import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ChatMessage } from "@/components/ChatMessage";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, Save, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ScoreGauge } from "@/components/ScoreGauge";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Message {
  role: "user" | "assistant";
  content: string;
  score?: number;
  feedback?: string;
}

export default function Treinamento() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const personaId = searchParams.get("personaId");
  const empresaId = searchParams.get("empresaId"); // empresa via parâmetro

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [empresa, setEmpresa] = useState<any>(null);
  const [currentPersonaId, setCurrentPersonaId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inicializadoRef = useRef(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const autoSavingRef = useRef(false);

  useEffect(() => {
    if (!inicializadoRef.current && user) {
      inicializadoRef.current = true;
      loadEmpresaFromParam();
    }
  }, [user, personaId, empresaId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Carrega empresa exclusivamente pelo parâmetro empresaId
  const loadEmpresaFromParam = async () => {
    if (!user) return;

    if (!empresaId) {
      toast({
        title: "Empresa não informada",
        description: "Abra o treinamento a partir do painel para carregar a empresa.",
        variant: "destructive",
      });
      navigate("/dashboard");
      return;
    }

    const { data: empresaData, error } = await supabase
      .from("empresas")
      .select("*")
      .eq("id", empresaId)
      .single();

    if (error || !empresaData) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados da empresa pelo parâmetro.",
        variant: "destructive",
      });
      navigate("/dashboard");
      return;
    }

    setEmpresa(empresaData);

    if (personaId) {
      setCurrentPersonaId(personaId);
      await carregarHistorico(personaId, empresaData);
    } else {
      iniciarConversa(empresaData);
    }
  };

  const carregarHistorico = async (pId: string, empresaData: any) => {
    setLoading(true);

    try {
      // Busca histórico da persona com score e feedback
      const { data: historico, error: erroHistorico } = await supabase
        .from("historicos")
        .select("role, content, score, feedback")
        .eq("persona_id", pId)
        .order("created_at");

      if (erroHistorico) throw erroHistorico;

      if (historico && historico.length > 0) {
        setMessages(
          historico.map((h) => ({
            role: h.role as "user" | "assistant",
            content: h.content,
            score: h.role === "assistant" ? (h as any).score ?? undefined : undefined,
            feedback: h.role === "assistant" ? (h as any).feedback ?? undefined : undefined,
          })),
        );
      } else {
        // Sem histórico: busca persona_gerada e mostra como primeira msg da IA
        const { data: personaData, error: erroPersona } = await supabase
          .from("personas")
          .select("persona_gerada")
          .eq("id", pId)
          .single();

        if (erroPersona) throw erroPersona;

        if (personaData) {
          setMessages([{ role: "assistant", content: personaData.persona_gerada }]);
        } else {
          toast({
            title: "Erro",
            description: "Persona não encontrada.",
            variant: "destructive",
          });
          navigate("/dashboard");
        }
      }
    } catch (error: any) {
      console.error("Erro ao carregar histórico:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o histórico.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const iniciarConversa = async (empresaData: any) => {
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("quick-action", {
        body: {
          empresa: {
            id: empresaData.id,
            produto: empresaData.produto,
            publico: empresaData.publico,
            proposta_valor: empresaData.proposta_valor,
          },
          requestType: 'persona_generation',
        },
      });

      if (error) throw error;

      if (!data?.choices?.[0]?.message?.content) {
        throw new Error("Resposta inválida da IA");
      }

      const parsed = parseResponse(data.choices[0].message.content);

      // Cria a persona associada à empresa do parâmetro
      // Sempre salva a persona, mesmo para usuários gratuitos (is_free_tier = true para plano start)
      if (user) {
        // Verificar plano da empresa para marcar is_free_tier
        const isFreeTier = empresaData.plano === 'start';
        
        const { data: persona, error: erroPersona } = await supabase
          .from("personas")
          .insert([
            {
              user_id: user.id,
              empresa_id: empresaData.id,
              persona_gerada: parsed.content,
              is_free_tier: isFreeTier,
            },
          ])
          .select()
          .single();

        if (erroPersona) {
          console.error("Erro ao criar persona:", erroPersona);
          toast({
            title: "Erro",
            description: "Não foi possível salvar a persona.",
            variant: "destructive",
          });
        } else if (persona) {
          setCurrentPersonaId(persona.id);
        }
      }

      setMessages([
        {
          role: "assistant",
          content: parsed.content,
          score: parsed.score,
          feedback: parsed.feedback,
        },
      ]);

      // Update local empresa state after edge function decremented feedback
      setEmpresa((prev: any) => prev ? { ...prev, feedbacks: Math.max(0, (prev.feedbacks ?? 1) - 1) } : prev);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseResponse = (rawText: string ): { content: string; score: number; feedback: string } => {
    console.log(rawText);
    // Normalização mínima
    const text = (rawText || "")
      .replace(/^\s*{\s*|\s*}\s*$/g, "") // remove chaves externas
      .replace(/\r\n?/g, "\n");          // normaliza quebras
  
    // Rótulos âncora
    // Aceita com ou sem ":" após o rótulo (Gemini às vezes omite)
    const reA = /(?:^|\n)\s*A\.\s*Mensagem_da_persona\s*:?\s*/i;
    const reB = /(?:^|\n)\s*B\.\s*Cr[ií]tica\s*:?\s*/i;
    const reC = /(?:^|\n)\s*C\.\s*Nota\s*:?\s*/i;
  
    // Localiza índices de início de cada seção
    const aMatch = reA.exec(text);
    const bMatch = reB.exec(text);
    const cMatch = reC.exec(text);
  
    // Função para recortar “do fim do cabeçalho atual até o próximo rótulo”
    const sliceSection = (headerMatch: RegExpExecArray | null, ...nextMatches: (RegExpExecArray | null)[]) => {
      if (!headerMatch) return "";
      const start = headerMatch.index + headerMatch[0].length;
      const candidates = nextMatches
        .filter(Boolean)
        .map(m => (m as RegExpExecArray).index)
        .concat(text.length)
        .filter(idx => idx > start);
      const end = Math.min(...candidates);
      return text.slice(start, end).trim();
    };
  
    const secA = sliceSection(aMatch, bMatch, cMatch);
    const secB = sliceSection(bMatch, aMatch, cMatch);
    const secC = sliceSection(cMatch, aMatch, bMatch);
  
    // Limpeza: remove colchetes externos e vírgulas finais/espúrias
    const stripBracketsAndCommas = (s: string) => {
      const trimmed = (s || "").trim();
      // remove vírgulas finais repetidas
      let cleaned = trimmed.replace(/\s*,\s*$/g, "").trim();
      // se vier totalmente embrulhado em [ ... ], remove
      const m = cleaned.match(/^\[\s*([\s\S]*?)\s*]$/);
      if (m) cleaned = m[1].trim();
      // se começar com [, remove só o primeiro [ (caso a IA não feche)
      cleaned = cleaned.replace(/^\[\s*/, "").replace(/\s*]$/, "");
      // limpa vírgula final de novo
      cleaned = cleaned.replace(/\s*,\s*$/g, "").trim();
      return cleaned;
    };
  
    const contentRaw  = stripBracketsAndCommas(secA);
    const feedbackRaw = stripBracketsAndCommas(secB);
    const scoreText   = stripBracketsAndCommas(secC);
    console.log({contentRaw, feedbackRaw, scoreText});

    /*calculo da nota*/
    const computeScore = (s: string): number => {
      if (!s) return 0;
    
      const norm = s.replace(/,/g, "."); // suporta vírgula decimal
    
      // 1) Primeiro tenta capturar rótulos explícitos (Empatia/Argumentos/Fechamento) com ":" OU "("...")"
      //    Ex.: "Empatia (30) | Argumentos: 40 | Fechamento(20)"
      let labeledSum = 0;
      let labeledFound = false;
      const labeledRe = /(?:Empatia|Empathy|Argumentos?|Arguments?|Fechamento|Closing)\s*(?:[:(]\s*([-+]?\d+(?:\.\d+)?)\s*\)?)?/gi;
      let lm: RegExpExecArray | null;
      while ((lm = labeledRe.exec(norm)) !== null) {
        const n = lm[1] ? parseFloat(lm[1]) : NaN;
        if (Number.isFinite(n)) {
          labeledSum += n;
          labeledFound = true;
        }
      }
      if (labeledFound) {
        return Math.max(0, Math.min(100, Math.round(labeledSum)));
      }
    
      // 2) Depois tenta números entre parênteses (sem rótulo)
      //    Ex.: "(10) | (20) | (5)"
      const parenNums = Array.from(norm.matchAll(/\(([-+]?\d+(?:\.\d+)?)\)/g))
        .map(m => parseFloat(m[1]))
        .filter(n => Number.isFinite(n));
      if (parenNums.length >= 2) {
        const sum = parenNums.reduce((a, b) => a + b, 0);
        return Math.max(0, Math.min(100, Math.round(sum)));
      }
    
      // 3) Depois tenta padrão com pipes sem parênteses
      //    Ex.: "10 | 20 | 5"
      const pipeParts = norm.split("|").map(x => parseFloat(x.trim())).filter(n => Number.isFinite(n));
      if (pipeParts.length >= 2) {
        const sum = pipeParts.reduce((a, b) => a + b, 0);
        return Math.max(0, Math.min(100, Math.round(sum)));
      }
    
      // 4) Fallback para número simples
      const single = norm.match(/-?\d+(?:\.\d+)?/);
      if (single) {
        const val = parseFloat(single[0]);
        return Math.max(0, Math.min(100, Math.round(val)));
      }
    
      return 0;
    };
  
    const score = computeScore(scoreText);
  
    // Fallbacks suaves: se algo faltou, tenta recuperar “best effort”
    const fallbackContent = contentRaw || (() => {
      const m = text.match(/Mensagem_da_persona\s*:\s*([\s\S]+)/i);
      return m ? stripBracketsAndCommas(m[1]) : text.trim();
    })();
  
    const fallbackFeedback = feedbackRaw || (() => {
      const m = text.match(/Cr[ií]tica\s*:\s*([\s\S]+)/i);
      return m ? stripBracketsAndCommas(m[1]) : "";
    })();

    console.log({fallbackContent, fallbackFeedback, score});
  
    return {
      content: fallbackContent,
      feedback: fallbackFeedback,
      score
    };
  };



  function formatAssistantForContext(msg: Message): string {
    const A = `A. Mensagem_da_persona: [${(msg.content || "").trim()}]`;
    const B = `B. Crítica: [${(msg.feedback || "").trim()}]`;
    const C = `C. Nota: [${typeof msg.score === "number" ? msg.score : 0}]`;
    return `{\n  ${A}\n  ${B}\n  ${C}\n}`;
  }

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    if (!empresa) {
      toast({
        title: "Empresa não carregada",
        description: "Reabra o treinamento pelo painel.",
        variant: "destructive",
      });
      return;
    }

    // Check if feedbacks are available before sending
    if (empresa.feedbacks <= 0) {
      toast({
        title: "Créditos esgotados",
        description: "Você não possui mais feedbacks disponíveis. Faça um upgrade do seu plano para continuar treinando.",
        variant: "destructive",
      });
      return;
    }

    const userMessage: Message = { role: "user", content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      // Build conversation history for backend (without system prompt - it's on backend now)
      const conversationHistory = messages.map((m) =>
        m.role === "assistant"
          ? ({ role: "assistant" as const, content: formatAssistantForContext(m) })
          : ({ role: "user" as const, content: m.content })
      );

      const { data, error } = await supabase.functions.invoke("quick-action", {
        body: {
          empresa: {
            id: empresa.id,
            produto: empresa.produto,
            publico: empresa.publico,
            proposta_valor: empresa.proposta_valor,
          },
          conversationHistory,
          userMessage: input,
          personaId: currentPersonaId,
          requestType: 'training',
        },
      });

      // Handle specific error responses from edge function
      if (error) {
        throw error;
      }
      
      // Check for error in data response (edge function returned error object)
      if (data?.error) {
        if (data.upgrade_required) {
          // Update local state to reflect no credits
          setEmpresa((prev: any) => prev ? { ...prev, feedbacks: 0 } : prev);
          toast({
            title: "Créditos esgotados",
            description: "Você não possui mais feedbacks disponíveis. Faça um upgrade do seu plano para continuar treinando.",
            variant: "destructive",
          });
          // Remove the user message that was just added
          setMessages((prev) => prev.slice(0, -1));
          return;
        }
        throw new Error(data.error);
      }
      
      if (!data?.choices?.[0]?.message?.content) {
        throw new Error("Resposta inválida da IA");
      }

      const parsed = parseResponse(data.choices[0].message.content);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: parsed.content,
          score: parsed.score,
          feedback: parsed.feedback,
        },
      ]);

      // Update local empresa state after edge function decremented feedback
      setEmpresa((prev: any) => prev ? { ...prev, feedbacks: Math.max(0, (prev.feedbacks ?? 1) - 1) } : prev);
    } catch (error: any) {
      // Remove the user message on error
      setMessages((prev) => prev.slice(0, -1));
      toast({
        title: "Erro",
        description: error.message || "Ocorreu um erro ao processar sua mensagem.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const salvarHistorico = async (silent = false) => {
    if (!currentPersonaId || messages.length === 0) {
      if (!silent) {
        toast({
          title: "Erro",
          description: "Nada para salvar.",
          variant: "destructive",
        });
      }
      return;
    }

    if (!silent) setLoading(true);

    try {
      await supabase.from("historicos").delete().eq("persona_id", currentPersonaId);

      const registros = messages.map((msg) => ({
        persona_id: currentPersonaId!,
        role: msg.role,
        content: msg.content,
        score: msg.role === "assistant" ? (msg.score ?? null) : null,
        feedback: msg.role === "assistant" ? (msg.feedback ?? null) : null,
        salvo_pelo_usuario: true,
      }));

      const { error } = await supabase.from("historicos").insert(registros);
      if (error) throw error;

      if (!silent) {
        toast({
          title: "Sucesso!",
          description: "Histórico salvo com sucesso!",
        });
      }
    } catch (error: any) {
      console.error("Erro ao salvar histórico:", error);
      if (!silent) {
        toast({
          title: "Erro",
          description: "Não foi possível salvar o histórico.",
          variant: "destructive",
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSalvarClick = () => {
    if (empresa?.plano === "start") {
      setUpgradeOpen(true);
      return;
    }
    salvarHistorico(false);
  };

  // Auto-save no plano max_pro após cada nova mensagem
  useEffect(() => {
    if (empresa?.plano !== "max_pro") return;
    if (!currentPersonaId || messages.length === 0 || loading) return;
    if (autoSavingRef.current) return;
    autoSavingRef.current = true;
    salvarHistorico(true).finally(() => {
      autoSavingRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, empresa?.plano, currentPersonaId, loading]);

  // Última métrica exibida na sidebar (última msg da IA)
  const { lastScore, lastFeedback } = useMemo(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    return {
      lastScore: lastAssistant?.score ?? 0,
      lastFeedback: lastAssistant?.feedback ?? "",
    };
  }, [messages]);

  return (
    <Layout title="Treinamento">
      <div className="max-w-6xl mx-auto">
        {/* Mobile: 1 coluna / Desktop: chat | sidebar (sticky à direita) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* ===== SIDEBAR: SCORE + FEEDBACK ===== */}
          <aside className="order-1 lg:order-2">
            <div className="sticky top-24 space-y-4">
              <div className="bg-card border border-border rounded-2xl shadow-lg p-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="bg-card border border-border rounded-2xl p-4 w-full"
                >
                  <h4 className="text-sm font-semibold mb-2 text-center text-muted-foreground">Sua avaliação</h4>
                  <ScoreGauge score={lastScore} />
                </motion.div>
              </div>

              <div className="bg-card border border-border rounded-2xl shadow-lg p-4">
                <h4 className="text-sm font-semibold mb-2 text-center text-muted-foreground">Feedback</h4>
                {lastFeedback ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{lastFeedback}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Aguardando o primeiro feedback…</p>
                )}
              </div>
            </div>
          </aside>

          {/* ===== CHAT ===== */}
          <div className="order-2 lg:order-1 bg-card border border-border rounded-2xl shadow-lg overflow-hidden flex flex-col">
            {/* Lista de mensagens (área rolável) */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 && !loading && (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <p>Iniciando treinamento...</p>
                </div>
              )}

              {messages.map((msg, idx) => (
                <ChatMessage
                  key={idx}
                  role={msg.role}
                  content={msg.content}
                />
              ))}

              {loading && (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Pensando...</span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input fixo no "rodapé" do card (fora do scroll das mensagens) */}
            <div className="border-t border-border p-4 bg-background/50 backdrop-blur">
              <div className="flex gap-3">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Digite sua resposta..."
                  className="resize-none"
                  rows={2}
                  disabled={loading}
                />
                <div className="flex flex-col gap-2">
                  <Button onClick={handleSend} disabled={loading || !input.trim()} className="self-end">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </Button>
                  {empresa?.plano !== "max_pro" && currentPersonaId && (
                    <Button
                      onClick={handleSalvarClick}
                      disabled={loading || !currentPersonaId}
                      variant="outline"
                      size="sm"
                    >
                      {empresa?.plano === "start" ? (
                        <Lock className="w-4 h-4 mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Salvar
                    </Button>
                  )}
                  {empresa?.plano === "max_pro" && currentPersonaId && (
                    <span className="text-xs text-muted-foreground text-center">
                      Salvamento<br />automático
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* ===== /CHAT ===== */}
        </div>
      </div>

      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Salvar histórico é um recurso premium</DialogTitle>
            <DialogDescription>
              O plano gratuito não permite salvar o histórico do treinamento.
              Faça upgrade para o plano <strong>Expert</strong> para salvar
              manualmente, ou para o <strong>Max Pro</strong> para ter
              salvamento automático a cada mensagem.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setUpgradeOpen(false)}>
              Agora não
            </Button>
            <Button onClick={() => navigate("/#planos")}>Ver planos</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
